import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { try_for_free } from "../../url";
import RefSiteFooter from "./RefSiteFooter";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import RefPricingTeaser from "./RefPricingTeaser";

// Assets
import security_img from "../../Assets/premium_security_illustration.png";
import checkmark_icon from "../../Assets/checkmark_icon.png";
import data_center_1 from "../../Assets/data_center_1.png";
import data_center_2 from "../../Assets/data_center_2.png";
import data_center_3 from "../../Assets/data_center_3.png";
import data_center_4 from "../../Assets/data_center_4.png";
import data_center_5 from "../../Assets/data_center_5.png";
import data_center_8 from "../../Assets/data_center_8.png";
import data_center_10 from "../../Assets/data_center_10.png";

const CERTIFICATION_IMAGES = [
  data_center_1,
  data_center_2,
  data_center_3,
  data_center_4,
  data_center_5,
  data_center_8,
  data_center_10,
];

const SECURITY_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap');
  :root {
    --rss-ink: #163A5A; --rss-ink2: #3D6A8C; --rss-ink3: #5E87A3;
    --rss-sky: #1A63A5; --rss-mint: #2AB6E8;
    --rss-secondary: #2AB6E8;
    --rss-secondary-deep: #1AA3D4;
    --rss-secondary-soft: #D9F4FC;
    --rss-secondary-muted: rgba(42,182,232,0.18);
    --rss-sky-light: #E8F4FC;
    --rss-sky-hover: #155a94;
    --rss-bg-dark: #0E2D48;
    --rss-white: #FFFFFF; --rss-surface: #F5F9FC;
    --rss-ff-head: 'Plus Jakarta Sans', system-ui, sans-serif; --rss-ff-body: 'Plus Jakarta Sans', system-ui, sans-serif;
  }
  .rss-page { font-family: var(--rss-ff-body); color: var(--rss-ink); background: var(--rss-white); }

  /* HERO */
  .rss-hero {
    padding: 64px 24px 32px;
    background: linear-gradient(180deg, #E8F4FC 0%, var(--rss-secondary-soft) 35%, #fff 100%);
    position: relative; overflow: hidden;
    text-align: center;
  }
  .rss-hero::before {
    content: ''; position: absolute; top: -150px; left: 50%; transform: translateX(-50%);
    width: 900px; height: 500px;
    background: radial-gradient(ellipse, rgba(26,99,165,.08) 0%, transparent 65%);
    pointer-events: none;
  }
  .rss-hero-orb {
    position: absolute; width: 400px; height: 400px; border-radius: 50%;
    background: radial-gradient(circle, rgba(42,182,232,0.15) 0%, transparent 60%);
    top: -100px; right: -100px; z-index: 0; filter: blur(20px);
  }
  .rss-hero-content { position: relative; z-index: 1; max-width: 800px; margin: 0 auto; }
  .rss-h1 { font-family: var(--rss-ff-head); font-size: clamp(38px, 6vw, 60px); font-weight: 800; letter-spacing: -.02em; line-height: 1.08; margin-bottom: 16px; }
  .rss-h1-grad { background: linear-gradient(135deg, var(--rss-sky), var(--rss-mint)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .rss-hero-sub { font-size: 18px; color: var(--rss-ink2); font-weight: 400; max-width: 600px; margin: 0 auto 16px; line-height: 1.6; }

  /* SECTIONS */
  .rss-section { padding: 80px 24px 64px; max-width: 1240px; margin: 0 auto; box-sizing: border-box; }
  .rss-section-alt { background: var(--rss-bg-dark); padding: 88px 32px 72px; }
  .rss-section-alt-inner { max-width: 1240px; margin: 0 auto; }
  .rss-section-alt .rss-h2 { color: #fff; }
  .rss-section-alt .rss-section-label { background: rgba(42,182,232,.15); color: var(--rss-mint); }

  .rss-grid { display: grid; grid-template-columns: 1.15fr 1fr; gap: 64px; align-items: center; }
  .rss-text-block { position: relative; z-index: 2; text-align: left; }
  .rss-section-label { display: inline-block; font-size: 11px; font-weight: 700; color: var(--rss-sky); background: var(--rss-sky-light); padding: 4px 10px; border-radius: 6px; letter-spacing: .08em; margin-bottom: 16px; text-transform: uppercase; }
  .rss-h2 { font-family: var(--rss-ff-head); font-size: clamp(30px, 4vw, 42px); font-weight: 800; line-height: 1.15; letter-spacing: -.02em; color: var(--rss-ink); margin-bottom: 20px; }
  .rss-p { font-size: 16.5px; color: var(--rss-ink2); line-height: 1.7; margin-bottom: 20px; font-weight: 400; }

  .rss-image-wrap { 
    position: relative; border-radius: 20px; z-index: 1; 
    transition: all 0.25s; border: 1px solid transparent;
  }
  .rss-image-wrap:hover { 
    transform: translateY(-4px); 
    box-shadow: 0 16px 40px rgba(26,99,165,.1); 
    border-color: rgba(26,99,165,.28); 
  }
  .rss-img { width: 100%; height: auto; display: block; border-radius: 20px; box-shadow: 0 24px 64px rgba(22,58,90,.12); }
  
  /* GRID: COMPLIANCE */
  .rss-comp-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; margin-top: 48px; }
  .rss-comp-card {
    background: linear-gradient(180deg, #ffffff 0%, var(--rss-surface) 100%);
    border: 1px solid rgba(26,99,165,.1); border-radius: 20px; padding: 32px 24px;
    text-align: center; transition: all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .rss-comp-card:hover { transform: translateY(-6px); box-shadow: 0 20px 40px rgba(0,0,0,.2); border-color: var(--rss-sky); }
  .rss-comp-icon { width: 56px; height: 56px; background: var(--rss-sky-light); border-radius: 16px; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; }
  .rss-comp-icon img { width: 28px; height: 28px; }
  .rss-comp-title { font-size: 19px; font-weight: 700; color: var(--rss-ink); margin-bottom: 12px; }
  .rss-comp-desc { font-size: 14.5px; color: var(--rss-ink2); line-height: 1.6; }

  /* SLIDER */
  .rss-slider-section { background: var(--rss-surface); padding: 72px 32px 64px; border-top: 1px solid #E8EAF2; overflow: hidden; }
  .rss-slider-wrap { max-width: 1240px; margin: 40px auto 0; }
  .rss-slider-card {
    background: #fff; height: 110px; display: flex !important; align-items: center; justify-content: center;
    border-radius: 16px; margin: 0 10px; box-shadow: 0 4px 16px rgba(22,58,90,0.05); border: 1px solid #E8EAF2;
    transition: all 0.3s; padding: 20px; box-sizing: border-box;
  }
  .rss-slider-card:hover { transform: translateY(-4px); box-shadow: 0 12px 24px rgba(22,58,90,0.1); border-color: var(--rss-sky); }
  .rss-slider-card img { max-width: 100%; max-height: 100%; object-fit: contain; }
  
  .slick-dots li button:before { color: var(--rss-sky) !important; font-size: 10px !important; }
  .slick-prev:before, .slick-next:before { color: var(--rss-sky) !important; font-size: 28px !important; }

  @media (max-width: 1024px) {
    .rss-grid { grid-template-columns: 1fr; gap: 48px; }
    .rss-comp-grid { grid-template-columns: repeat(2, 1fr); }
  }
  @media (max-width: 600px) {
    .rss-hero { padding: 48px 20px 32px; }
    .rss-h1 { font-size: 34px; }
    .rss-hero-sub { font-size: 15px; }
    .rss-h2 { font-size: 28px; }
    .rss-p { font-size: 15px; }
    .rss-section, .rss-section-alt, .rss-slider-section { padding: 48px 20px; }
    .rss-comp-grid { grid-template-columns: 1fr; }
  }
`;

function RefSecurity() {
  const { t } = useTranslation('security');
  const navigate = useNavigate();

  const securityMeasures = [
    {
      title: t('measures.items.encryption.title'),
      desc: t('measures.items.encryption.desc'),
    },
    {
      title: t('measures.items.storage.title'),
      desc: t('measures.items.storage.desc'),
    },
    {
      title: t('measures.items.assessments.title'),
      desc: t('measures.items.assessments.desc'),
    },
    {
      title: t('measures.items.standards.title'),
      desc: t('measures.items.standards.desc'),
    },
  ];

  const complianceEfforts = [
    {
      title: t('compliance.items.legal.title'),
      desc: t('compliance.items.legal.desc'),
    },
    {
      title: t('compliance.items.privacy.title'),
      desc: t('compliance.items.privacy.desc'),
    },
    {
      title: t('compliance.items.risk.title'),
      desc: t('compliance.items.risk.desc'),
    },
  ];

  const sliderSettings = {
    dots: true,
    arrows: true,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 3 } },
      { breakpoint: 768, settings: { slidesToShow: 2, arrows: false } },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          arrows: false,
          centerMode: true,
          centerPadding: "20px",
        },
      },
    ],
  };

  return (
    <div className="rss-page">
      <style>{SECURITY_STYLES}</style>

      {/* HERO */}
      <div className="rss-hero">
        <div className="rss-hero-orb" />
        <div className="rss-hero-content">
          <h1 className="rss-h1">
            {t('hero.titlePrefix')}<span className="rss-h1-grad">{t('hero.titleHighlight')}</span>
          </h1>
          <p className="rss-hero-sub">
            {t('hero.subtitle')}
          </p>
        </div>
      </div>

      {/* SECURITY MEASURES */}
      <div className="rss-section">
        <div className="rss-grid">
          <div className="rss-text-block">
            <div className="rss-section-label">{t('measures.label')}</div>
            <h2 className="rss-h2" style={{ textAlign: "left" }}>
              {t('measures.title')}
            </h2>
            <p className="rss-p">
              {t('measures.description')}
            </p>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "24px",
                marginTop: "32px",
              }}
            >
              {securityMeasures.map((item, i) => (
                <div key={i} style={{ display: "flex", gap: "16px" }}>
                  <div
                    className="rss-measure-icon"
                    style={{
                      width: "40px",
                      height: "40px",
                      background: "var(--rss-sky-light)",
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0
                    }}
                  >
                    <img
                      src={checkmark_icon}
                      alt="Check"
                      style={{ width: "20px", height: "20px" }}
                    />
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: 700,
                        color: "var(--rss-ink)",
                        marginBottom: "4px"
                      }}
                    >
                      {item.title}
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "var(--rss-ink2)",
                        lineHeight: 1.55
                      }}
                    >
                      {item.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rss-image-wrap">
            <img
              src={security_img}
              alt={t('hero.titleHighlight')}
              className="rss-img"
            />
          </div>
        </div>
      </div>

      {/* COMPLIANCE EFFORTS */}
      <div className="rss-section-alt">
        <div className="rss-section-alt-inner">
          <div style={{ textAlign: "center" }}>
            <div className="rss-section-label">{t('compliance.label')}</div>
            <h2 className="rss-h2">{t('compliance.title')}</h2>
          </div>
          <div className="rss-comp-grid">
            {complianceEfforts.map((item, i) => (
              <div className="rss-comp-card" key={i}>
                <div className="rss-comp-icon">
                  <img src={checkmark_icon} alt="Check" />
                </div>
                <div className="rss-comp-title">{item.title}</div>
                <div className="rss-comp-desc">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* DATA CENTERS */}
      <div className="rss-slider-section">
        <div
          style={{ textAlign: "center", maxWidth: "800px", margin: "0 auto" }}
        >
          <h2 className="rss-h2" style={{ marginBottom: "16px" }}>
            {t('dataCenters.title')}
          </h2>
          <p
            style={{
              fontSize: "18px",
              color: "var(--rss-ink2)",
              lineHeight: 1.6,
            }}
          >
            {t('dataCenters.statementPrefix')}
            <strong>{t('dataCenters.standardsHighlight')}</strong>
            {t('dataCenters.for')}
            <br />
            <strong>{t('dataCenters.privacyHighlight')}</strong>
            {t('dataCenters.period')}
          </p>
        </div>

        <div className="rss-slider-wrap">
          <Slider {...sliderSettings}>
            {CERTIFICATION_IMAGES.map((imgSrc, i) => (
              <div key={i} style={{ padding: "10px" }}>
                <div className="rss-slider-card">
                  <img
                    src={imgSrc}
                    alt={t('dataCenters.certAlt', { index: i + 1 })}
                  />
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </div>

      {/* PRICING TEASER */}
      <RefPricingTeaser />

      {/* FOOTER */}
      <RefSiteFooter />
    </div>
  );
}

export default RefSecurity;
